import type { Column } from '@shared/types/data-table'
import type { ProgramProduct } from '@features/programs/types/program-product.types'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import { createStatusColumn, createDateColumn, createTextColumn } from './column-factories'

const categoryLabels: Record<string, string> = {
  consumer: 'Consumer',
  business: 'Business',
  premium: 'Premium',
  basic: 'Basic',
}

export const PROGRAM_PRODUCT_COLUMNS: Record<string, Column<ProgramProduct>> = {
  name: createTextColumn<ProgramProduct>('name', 'Product Name', 220),
  status: createStatusColumn<ProgramProduct>('program-product'),
  programName: createTextColumn<ProgramProduct>('programName', 'Program', 200),
  category: createTextColumn<ProgramProduct>(
    'category', 'Category', 120, undefined, undefined,
    (value: string) => categoryLabels[value] || value
  ),
  currency: createTextColumn<ProgramProduct>('currency', 'Currency', 90),
  annualFee: createTextColumn<ProgramProduct>(
    'annualFee' as keyof ProgramProduct, 'Annual Fee', 110, undefined, undefined,
    (value: number) => value === 0 ? 'Free' : `$${value.toFixed(2)}`
  ),
  interestRate: createTextColumn<ProgramProduct>(
    'interestRate' as keyof ProgramProduct, 'Interest Rate', 120, undefined, undefined,
    (value: number) => value === 0 ? 'N/A' : `${value.toFixed(2)}%`
  ),
  rewardsRate: createTextColumn<ProgramProduct>(
    'rewardsRate' as keyof ProgramProduct, 'Rewards', 100, undefined, undefined,
    (value: number) => value === 0 ? 'None' : `${value.toFixed(1)}%`
  ),
  enrollmentCount: createTextColumn<ProgramProduct>(
    'enrollmentCount' as keyof ProgramProduct, 'Enrollments', 120, undefined, undefined,
    (value: number) => value?.toLocaleString() ?? '0'
  ),
  createdDate: createDateColumn<ProgramProduct>('createdDate', 'Created', 120),
}

export const PROGRAM_PRODUCT_STATUS_CONFIG = {
  all: ['name', 'status', 'programName', 'category', 'annualFee', 'interestRate', 'rewardsRate', 'enrollmentCount', 'createdDate'],
  active: ['name', 'status', 'programName', 'category', 'annualFee', 'interestRate', 'rewardsRate', 'enrollmentCount', 'createdDate'],
  draft: ['name', 'status', 'programName', 'category', 'annualFee', 'createdDate'],
  deprecated: ['name', 'status', 'programName', 'category', 'enrollmentCount', 'createdDate'],
  inactive: ['name', 'status', 'programName', 'category', 'createdDate'],
} as const

export const getProgramProductColumns = (status: string = 'all'): Column<ProgramProduct>[] => {
  const keys = PROGRAM_PRODUCT_STATUS_CONFIG[status as keyof typeof PROGRAM_PRODUCT_STATUS_CONFIG] || PROGRAM_PRODUCT_STATUS_CONFIG.all
  return keys.map(key => PROGRAM_PRODUCT_COLUMNS[key]).filter(Boolean)
}

export const getProgramProductColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(PROGRAM_PRODUCT_STATUS_CONFIG[status as keyof typeof PROGRAM_PRODUCT_STATUS_CONFIG] || PROGRAM_PRODUCT_STATUS_CONFIG.all)
  return Object.entries(PROGRAM_PRODUCT_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['name', 'status'].includes(key),
    order: index
  }))
}
